/**
 * US-65: модалка «Забыли пароль» с страницы логина.
 *
 * Пользователь вводит email → бэк ищет Employee по email и шлёт ссылку.
 * Всегда показываем generic-сообщение «если email зарегистрирован,
 * мы прислали инструкции» — это защита от перечисления адресов.
 */

import { useState } from "react";
import { Modal, Stack, TextInput, Button, Alert } from "@mantine/core";
import { Check } from "lucide-react";

import { requestPasswordReset } from "../api/client";

interface Props {
  opened: boolean;
  onClose: () => void;
}

export function ForgotPasswordModal({ opened, onClose }: Props) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  function reset() {
    setEmail("");
    setDone(false);
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      setDone(true);
    } catch {
      // Endpoint всегда отдаёт 200 если email непустой — сюда мы попадаем
      // только в случае ошибки сети. Маскируем через тот же ответ.
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Восстановление пароля" centered size="sm">
      <Stack gap="md">
        {done ? (
          <Alert color="green" icon={<Check size={16} />} data-testid="forgot-success">
            Если адрес электронной почты зарегистрирован в&nbsp;Системе учёта
            таймшитов, мы&nbsp;направили Вам инструкции по&nbsp;сбросу пароля.
            Проверьте почту (и&nbsp;папку «Нежелательные»), письмо может быть
            доставлено через несколько минут.
          </Alert>
        ) : (
          <>
            <TextInput
              label="Email"
              description="Введите, пожалуйста, адрес корпоративной электронной почты для Вашей учётной записи."
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
              autoComplete="email"
              type="email"
              data-testid="forgot-email"
            />
            <Button
              onClick={() => void handleSubmit()}
              loading={submitting}
              disabled={!email || submitting}
              data-testid="forgot-submit"
            >
              Прислать ссылку
            </Button>
          </>
        )}
      </Stack>
    </Modal>
  );
}
